/**
 * Platform (outbox) contracts: subscriber consumer names, the dead-letter admin
 * API (05-api.md, "Admin": `GET /v1/admin/dead-letters`,
 * `POST /v1/admin/dead-letters/{id}/retry`) and the event its retry emits.
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent, eventTypeSchema, eventVersionSchema } from './events.js';
import { uuidSchema } from './ids.js';

/**
 * A subscriber's stable consumer name, `module.handler_name` in snake_case
 * (`realtime.project_order`). It keys `event_consumption` (dedupe) and names the
 * subscriber's own queue, so it must never change once events were consumed.
 */
export const CONSUMER_NAME_PATTERN = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
export const consumerNameSchema = z.string().max(100).regex(CONSUMER_NAME_PATTERN);

/** Dead-letter id: `<eventId>.<consumer>`, unique per event and subscriber. */
export const DEAD_LETTER_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
export const deadLetterIdSchema = z.string().max(150).regex(DEAD_LETTER_ID_PATTERN);

/**
 * A subscriber job that failed for good: it exhausted its attempts, failed
 * unrecoverably, or was interrupted (stalled) too many times.
 */
export const deadLetterSchema = z.object({
  id: deadLetterIdSchema,
  consumer: consumerNameSchema,
  queue: z.string(),
  eventId: uuidSchema,
  eventType: eventTypeSchema,
  eventVersion: eventVersionSchema,
  /** The tenant of the event (dead letters are listed across companies). */
  companyId: uuidSchema,
  outletId: uuidSchema.nullable(),
  requestId: z.string(),
  attemptsMade: z.int().min(0),
  failedReason: z.string(),
  failedAt: isoTimestampSchema,
});

export type DeadLetter = z.output<typeof deadLetterSchema>;

export const deadLetterRetryResultSchema = z.object({
  id: deadLetterIdSchema,
  eventId: uuidSchema,
  consumer: consumerNameSchema,
  /**
   * `requeued`: the failed job runs again from attempt 1. A dead letter that is no
   * longer failed is 404 (`NOT_FOUND`); one another request retried at the same
   * moment is 409 (`CONFLICT`).
   */
  outcome: z.enum(['requeued']),
});

/** An administrator re-queued a dead-lettered subscriber job. Company-level event of the admin's company. */
export const deadLetterRetriedV1 = defineEvent(
  'dead_letter.retried',
  1,
  z.object({
    deadLetterId: deadLetterIdSchema,
    consumer: consumerNameSchema,
    eventId: uuidSchema,
    eventType: eventTypeSchema,
    eventVersion: eventVersionSchema,
    eventCompanyId: uuidSchema,
  }),
);
